// Firebase client init + helper to register an FCM token with the backend.
// Quietly no-ops when VAPID key is missing or notifications aren't supported.
//
// Native (Capacitor) path uses APNs/FCM through @capacitor/push-notifications;
// web path uses Firebase JS SDK. Both endpoints register on the same backend.
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { api } from '@/lib/api';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Capacitor runtime detection — Capacitor.isNativePlatform() is the canonical
// way to branch between web and native. Wrapped in a try so this file still
// works in pure-web builds where @capacitor/core isn't imported.
let _isNative = false;
try {
  // require() at runtime so the web bundle works even before @capacitor/core
  // is installed in older environments.
  const cap = require('@capacitor/core');
  _isNative = !!(cap && cap.Capacitor && cap.Capacitor.isNativePlatform && cap.Capacitor.isNativePlatform());
} catch {
  _isNative = false;
}

let _app = null;
let _messaging = null;

function getApp() {
  if (_app) return _app;
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) return null;
  _app = getApps()[0] || initializeApp(firebaseConfig);
  return _app;
}

async function getMessagingSafe() {
  if (_messaging) return _messaging;
  try {
    if (!(await isSupported())) return null;
  } catch {
    return null;
  }
  const app = getApp();
  if (!app) return null;
  _messaging = getMessaging(app);
  return _messaging;
}

async function registerNative() {
  try {
    const { PushNotifications } = require('@capacitor/push-notifications');
    const isIOS = window?.Capacitor?.getPlatform?.() === 'ios';
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== 'granted') {
      const req = await PushNotifications.requestPermissions();
      if (req.receive !== 'granted') return null;
    }
    return new Promise((resolve) => {
      let resolved = false;
      const finish = (val) => { if (!resolved) { resolved = true; resolve(val); } };

      const postToken = async (token) => {
        try {
          await api.post('/fcm/register-token', {
            token,
            platform: isIOS ? 'ios' : 'android',
            device_label: navigator.userAgent.slice(0, 100),
          });
          finish(token);
        } catch (e) {
          console.warn('[fcm-native] backend register failed', e?.message || e);
          finish(null);
        }
      };

      if (isIOS) {
        // iOS: the FCM token is bridged from AppDelegate.swift via a JS
        // window event. The Capacitor 'registration' event delivers the
        // raw APNs hex which is NOT a valid FCM token and we MUST ignore.
        const handler = (e) => {
          const token = (e && e.detail) || window.__FCM_TOKEN__;
          if (token) postToken(token);
        };
        window.addEventListener('fcm-token', handler);
        // Race: if AppDelegate fired before we attached the listener, the
        // token is already on window.
        if (window.__FCM_TOKEN__) handler({ detail: window.__FCM_TOKEN__ });
      } else {
        // Android: Capacitor's plugin already returns the FCM token directly.
        PushNotifications.addListener('registration', async (token) => {
          await postToken(token.value);
        });
      }

      PushNotifications.addListener('registrationError', (err) => {
        console.warn('[fcm-native] registration error', err);
        finish(null);
      });
      PushNotifications.register();
      // Safety timeout: 15s
      setTimeout(() => finish(null), 15000);
    });
  } catch (e) {
    console.warn('[fcm-native] init failed', e?.message || e);
    return null;
  }
}

async function registerWeb() {
  if (!VAPID_KEY) {
    console.info('[fcm] VAPID key missing — skipping push registration');
    return null;
  }
  if (typeof window === 'undefined' || !('Notification' in window)) return null;
  if (!('serviceWorker' in navigator)) return null;
  const messaging = await getMessagingSafe();
  if (!messaging) return null;
  if (Notification.permission === 'denied') return null;
  if (Notification.permission !== 'granted') {
    const res = await Notification.requestPermission();
    if (res !== 'granted') return null;
  }
  const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: swReg,
  });
  if (!token) return null;
  await api.post('/fcm/register-token', {
    token,
    platform: 'web',
    device_label: navigator.userAgent.slice(0, 100),
  });
  return token;
}

/**
 * Ensure push notifications are registered for the current platform.
 * Web → Firebase JS SDK + service worker. Native → APNs/FCM via Capacitor.
 * Returns the token on success, null otherwise. Never throws.
 */
export async function registerForPushNotifications() {
  try {
    return _isNative ? await registerNative() : await registerWeb();
  } catch (e) {
    console.warn('[fcm] register failed', e?.message || e);
    return null;
  }
}

/**
 * Subscribe to foreground messages (app open in tab / foreground native).
 * Returns an unsubscribe function.
 */
export async function onForegroundMessage(handler) {
  if (_isNative) {
    try {
      const { PushNotifications } = require('@capacitor/push-notifications');
      const l = await PushNotifications.addListener('pushNotificationReceived', handler);
      return () => l.remove();
    } catch {
      return () => {};
    }
  }
  const messaging = await getMessagingSafe();
  if (!messaging) return () => {};
  return onMessage(messaging, handler);
}
